import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import type { AssetWithUrls } from '@shared/types';
import { KIND_ICON, KIND_TINT, cn, formatFileSize } from '@/lib/format';
import { useFavouriteIds, useToggleFavourite } from '@/lib/queries';

/**
 * The asset grid. Thumbnails for images; a kind icon tile for everything else.
 * Each tile links to the asset detail, shows a confirmed-tag badge, and has a
 * hover/active favourite star.
 */
export function AssetGrid({ assets }: { assets: (AssetWithUrls & { aiTags?: { confirmed: boolean } | null })[] }) {
  const { data: favIds } = useFavouriteIds();
  const toggleFav = useToggleFavourite();

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 p-4">
      {assets.map((a) => {
        const Icon = KIND_ICON[a.kind];
        const fav = favIds?.has(a.id) ?? false;
        return (
          <Link
            key={a.id}
            to="/assets/$assetId"
            params={{ assetId: a.id }}
            className="group relative flex flex-col overflow-hidden rounded-[var(--radius-card)] surface-card border line transition-shadow hover:shadow-md"
          >
            <div className="relative flex aspect-square items-center justify-center surface-sunken">
              {a.thumbUrl ? (
                <img src={a.thumbUrl} alt={a.fileName} loading="lazy" className="size-full object-cover" />
              ) : (
                <Icon className={cn('size-12', KIND_TINT[a.kind])} aria-hidden />
              )}
              {a.aiTags?.confirmed && (
                <span className="absolute right-1.5 top-1.5 rounded-full bg-brand-500 px-1.5 py-px text-[9px] font-semibold text-white">✓ Tags</span>
              )}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav.mutate(a.id); }}
                className={cn(
                  'absolute left-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-black/40 text-white/90 backdrop-blur transition-opacity hover:bg-black/60',
                  fav ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
                title={fav ? 'Remove from favourites' : 'Add to favourites'}
                aria-label={fav ? 'Remove from favourites' : 'Add to favourites'}
              >
                <Star className={cn('size-4', fav && 'fill-amber-400 text-amber-400')} />
              </button>
            </div>
            <div className="flex flex-col gap-0.5 p-2">
              <span className="truncate text-[12px] font-medium text-strong" title={a.fileName}>{a.fileName}</span>
              <span className="text-[10px] text-faint">{a.kind} · {formatFileSize(a.sizeBytes)}</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
